import React, {FC, useEffect, useState} from "react"

import {IconTypes} from "@irati/ui/src/Icon"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {PaymentCurrencyType, ReportingRecognition, WalletEntityType} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import {Body} from "@irati/ui/src/Typography"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"

import walletService from "@irati/wallet/graph/wallet"
import authentication from "@irati/auth"
import {getNormalisedValue} from "@irati/utils/currency/formatCurrency"
import {adminDashboardTracking} from "@irati-tracking/admin"
import Widget from "../Widget"
import BoldNumberSmallText from "../BoldNumberSmallText"
import RecognitionUser from "./RecognitionUser"
import RecognitionModal from "./RecognitionModal"

interface Props extends InjectIntl {
  recognition: ReportingRecognition
}

const RecognitionWidget: FC<Props> = ({recognition, formatMessage}) => {
  const theme = useTheme()
  const [show, setShow] = useState<boolean>(false)
  const [rewardPoints, setRewardPoints] = useState<number>(0)

  useEffect(() => {
    walletService
      .get(authentication.user().uuid, PaymentCurrencyType.Rp, WalletEntityType.Reward, {fetchPolicy: "network-only"})
      .then(({amount}) => {
        if (amount) {
          const value = getNormalisedValue(true, PaymentCurrencyType.Rp, amount)
          setRewardPoints(value)
        }
      })
  }, [])

  const buttonText = rewardPoints
    ? formatMessage(
        {id: "admin.components.dashboard.reporting.recognition.buttonText.RP"},
        {rewardPoints: rewardPoints >= 10000 ? `${parseFloat((rewardPoints / 1000).toFixed(1))}K` : rewardPoints}
      )
    : formatMessage({id: "admin.components.dashboard.reporting.recognition.buttonText.noRP"})

  const handleClick = () => {
    const action = rewardPoints ? "cta_spend_reward" : "cta_buy_reward"
    adminDashboardTracking.recognitionInsights(action)
    setShow(true)
  }

  return (
    <>
      <Widget
        title="admin.components.dashboard.reporting.recognition.title"
        icon={IconTypes.companyBenefit}
        buttonText={buttonText}
        onClick={handleClick}
        usePrimaryBtn
        iconColor={theme.colors.primary1}>
        {recognition?.totalRecognitions ? (
          <div>
            <BoldNumberSmallText boldValue={recognition.totalRecognitions} text="recognitions" />
            <Flex direction="column" gap={1} marginTop={2}>
              {recognition?.topGivingUser?.user && (
                <RecognitionUser
                  user={recognition.topGivingUser.user}
                  text="admin.components.dashboard.reporting.recognition.given"
                  value={recognition.topGivingUser.total}
                />
              )}
              {recognition?.topReceivedUser?.user && (
                <RecognitionUser
                  user={recognition.topReceivedUser.user}
                  text="admin.components.dashboard.reporting.recognition.received"
                  value={recognition.topReceivedUser.total}
                />
              )}
            </Flex>
          </div>
        ) : (
          <>
            <Flex justifyContent="center" marginBottom={1}>
              <img width="100px" src={formatMessage({id: "admin.components.dashboard.reporting.recognition.img"})} />
            </Flex>
            <Body small>
              <FormattedMessage id="admin.components.dashboard.reporting.recognition.comingSoon.text" />
            </Body>
          </>
        )}
      </Widget>
      <RecognitionModal rewardPoints={rewardPoints} show={show} onHide={() => setShow(false)} />
    </>
  )
}

export default injectIntl(RecognitionWidget)
